/**
 * Aviso de mantenimiento y panel de estado (GDD 15.11).
 *
 * Lee un `status.json` ESTATICO. No consulta la API: la landing no sabe si el
 * juego esta arriba ni debe saberlo por esa via, porque preguntarselo la
 * acoplaria justo a lo que puede estar caido. Un fichero estatico en el mismo
 * nginx sigue respondiendo cuando PHP, Postgres y Redis no.
 *
 * QUIEN LO ESCRIBE, Y POR QUE ESO OBLIGA A UNA CADUCIDAD
 * ------------------------------------------------------
 * El fichero lo publica `php artisan estado:publicar` desde el planificador,
 * sondeando los cuatro componentes. Desde esta pagina sigue sin haber medicion
 * en vivo —el navegador lee un fichero y nada mas—, pero quien lo rellena ya no
 * es una persona: es un proceso, y un proceso se muere.
 *
 * Ahi esta el problema que resuelve la mitad de este modulo. Un publicador
 * muerto deja el fichero congelado con lo ultimo que midio, que casi siempre
 * sera "todo operativo". El panel seguiria afirmandolo con total seguridad
 * mientras el juego lleva horas caido — que es exactamente cuando alguien entra
 * a mirarlo. Un publicador sin caducidad es PEOR que el fichero escrito a mano
 * que habia antes, porque ademas tiene aspecto de estar vivo.
 *
 * Por eso `actualizado` dejo de ser decorativo: es la condicion de que los
 * componentes se lean siquiera. Sin el, ilegible, o pasado de fecha, las cuatro
 * filas se quedan en "sin datos" y el pie dice por que.
 *
 * Todo lo de aqui es decoracion sobre una pagina que ya esta completa: si este
 * modulo no se ejecuta —JavaScript desactivado, red a medias, error de parseo—
 * lo que queda es la landing normal con el boton JUGAR funcionando y las filas
 * de estado en "sin datos". Ese es el fallo seguro correcto: es peor esconder el
 * boton por un `status.json` que no se pudo leer que no avisar de un
 * mantenimiento.
 */

/** Los cuatro valores que entiende el panel. Cualquier otro cae en "sin datos". */
type Nivel = 'ok' | 'degradado' | 'caido' | 'mantenimiento'

/**
 * Margen maximo que se le concede al fichero antes de dejar de creerselo.
 *
 * Diez minutos contra un publicador que corre CADA MINUTO, o sea diez pasadas.
 * El numero sale de dos exigencias opuestas:
 *
 *   - Una pasada perdida no puede vaciar el panel. Un reinicio del contenedor,
 *     una sonda lenta, un candado que tarda en soltarse: eso pasa, y hacer
 *     parpadear las cuatro filas a "sin datos" cada vez entrenaria a todo el
 *     mundo a no fiarse del panel, que es la unica forma de romperlo del todo.
 *   - Diez pasadas seguidas perdidas no son ruido: es un publicador roto, y a
 *     partir de ahi el fichero no se distingue de una mentira.
 *
 * La relacion es "umbral = 10 x intervalo del planificador". Si alguien cambia
 * ese intervalo en `backend/routes/console.php`, este numero se queda corto o
 * largo y hay que moverlo con el. Esta escrito en los dos sitios a proposito.
 *
 * VIVE AQUI Y NO EN EL JSON, y es deliberado: este umbral es lo que AUDITA al
 * publicador, y no se le pide a un sospechoso que fije su propia coartada. Si el
 * numero viajara en el fichero, un publicador roto —o una plantilla vieja, o un
 * dedo— que escribiera un margen enorme haria que la pagina se creyera un
 * fichero congelado para siempre. La garantia tiene que estar en el artefacto
 * que se despliega con la pagina, no en el que escribe el proceso vigilado.
 */
const CADUCIDAD_MAX_MS = 10 * 60 * 1000

type Componente = {
  /** Casa con el `data-comp` de la fila ya escrita en el HTML. */
  id?: string
  estado?: string
}

type Estado = {
  mantenimiento?: boolean
  mensaje?: string
  /** ISO 8601. Cuando VENCE el aviso de mantenimiento; ver `pintarAviso`. */
  hasta?: string
  /** ISO 8601. Cuando se publico este fichero. Sin esto no se lee nada mas. */
  actualizado?: string
  /**
   * Segundos de vigencia que pide el fichero. Solo puede ACORTAR el margen,
   * nunca alargarlo (ver el `Math.min` de abajo): asi el despliegue puede
   * endurecer la vigencia durante una ventana critica sin reconstruir la
   * imagen, y no puede relajarla por accidente. Que la flexibilidad exista solo
   * en la direccion segura es justo lo que la hace aceptable.
   */
  caduca_en?: number
  componentes?: Componente[]
}

/**
 * Etiqueta visible por nivel, en los dos idiomas de la pagina. El JSON lleva
 * claves cortas y estables y el texto vive aqui: cambiar "operativo" por otra
 * palabra no deberia obligar a reeditar el fichero que monta produccion.
 *
 * Las cuatro claves son un CONTRATO COMPARTIDO con el backend
 * (`App\Domain\Enums\NivelDeEstado`). Cambiar una de las dos partes sin la otra
 * no da error en ningun sitio: el nivel deja de reconocerse y la fila cae a
 * "sin datos" en silencio.
 *
 * El idioma se lee de `<html lang>`, que el generador de paginas ya deja puesto
 * segun la version. Es la unica señal que necesita: la pagina inglesa y la
 * castellana son ficheros distintos, no un mismo documento que cambia.
 */
const ETIQUETAS: Record<string, Record<Nivel, string>> = {
  es: {
    ok: 'operativo',
    degradado: 'degradado',
    caido: 'caído',
    mantenimiento: 'en mantenimiento',
  },
  en: {
    ok: 'operational',
    degradado: 'degraded',
    caido: 'down',
    mantenimiento: 'under maintenance',
  },
}

/** Mensaje de reserva cuando `status.json` marca mantenimiento y no trae texto. */
const MANTENIMIENTO: Record<string, string> = {
  es: 'El juego está en mantenimiento. Vuelve en un rato.',
  en: 'The game is under maintenance. Check back in a while.',
}

const idioma = document.documentElement.lang === 'en' ? 'en' : 'es'
const ETIQUETA = ETIQUETAS[idioma]!

const aviso = document.querySelector<HTMLElement>('[data-aviso]')
const texto = document.querySelector<HTMLElement>('[data-aviso-texto]')
const pie = document.querySelector<HTMLElement>('[data-salud-pie]')
const fecha = document.querySelector<HTMLElement>('[data-salud-fecha]')
const caducado = document.querySelector<HTMLElement>('[data-salud-caducado]')

function esNivel(valor: unknown): valor is Nivel {
  return typeof valor === 'string' && valor in ETIQUETA
}

type Vigencia = {
  /** Cuando se publico, si se pudo leer. Se enseña aunque haya caducado. */
  sello: Date | null
  /** Si los componentes se pueden creer. */
  vigente: boolean
}

/**
 * Decide si el fichero es lo bastante reciente como para creerse sus mediciones.
 *
 * Los tres caminos que dan `vigente: false` —sin sello, sello ilegible, sello
 * fuera de margen— acaban en lo mismo: las cuatro filas se quedan con el "sin
 * datos" que el HTML ya trae escrito. No hay codigo de pintado para ese caso; lo
 * que hay es dejar de llamar a `pintarComponentes`.
 */
function vigencia(estado: Estado): Vigencia {
  if (typeof estado.actualizado !== 'string') return { sello: null, vigente: false }

  const sello = new Date(estado.actualizado)
  if (Number.isNaN(sello.getTime())) return { sello: null, vigente: false }

  const declarado =
    typeof estado.caduca_en === 'number' && estado.caduca_en > 0
      ? estado.caduca_en * 1000
      : CADUCIDAD_MAX_MS

  const margen = Math.min(declarado, CADUCIDAD_MAX_MS)

  // Valor absoluto, no una resta con signo: un sello en el FUTURO descalifica
  // igual que uno viejo. Puede ser el reloj del visitante desajustado o un
  // publicador con la zona horaria mal, y en los dos casos la comparacion que
  // sigue deja de significar nada. Cuando no se puede medir la antiguedad, la
  // respuesta es "no lo se", que es la direccion segura.
  const desfase = Math.abs(Date.now() - sello.getTime())

  return { sello, vigente: desfase <= margen }
}

function pintarAviso(estado: Estado): void {
  if (!aviso || !texto) return
  if (estado.mantenimiento !== true) return

  // `hasta` es la caducidad del AVISO, y no tiene nada que ver con la del
  // fichero. La asimetria es deliberada: los componentes son una medicion cuya
  // unica prueba de vida es el latido del publicador, mientras que esto es una
  // frase escrita por una persona que ya lleva dentro su propio vencimiento. Un
  // fichero congelado sigue avisando, y esta bien que lo haga: si nadie publica
  // desde hace una semana, lo probable es que el juego este caido de verdad.
  //
  // Lo que si vence es la hora que alguien escribio. Es lo que evita que el
  // aviso de un mantenimiento de hace tres semanas siga en la portada porque
  // nadie se acordo de apagarlo.
  if (typeof estado.hasta === 'string') {
    const fin = new Date(estado.hasta)
    if (!Number.isNaN(fin.getTime()) && fin.getTime() < Date.now()) return
  }

  // `mensaje` viene del despliegue en un solo idioma; se enseña tal cual porque
  // es informacion concreta ("volvemos a las 18:00") y traducirla no es cosa de
  // esta pagina. La reserva si sigue al idioma del documento.
  texto.textContent = estado.mensaje ?? MANTENIMIENTO[idioma]!
  aviso.hidden = false
}

function pintarComponentes(estado: Estado): void {
  if (!Array.isArray(estado.componentes)) return

  for (const componente of estado.componentes) {
    if (!componente?.id) continue

    // Se busca la fila; no se crea. Las filas las decide el HTML, asi que un
    // `id` que no exista en la pagina se ignora en silencio en vez de aparecer
    // sin etiqueta traducida ni sitio pensado en el orden.
    const fila = document.querySelector<HTMLElement>(
      `[data-comp="${CSS.escape(componente.id)}"]`,
    )
    const valor = fila?.querySelector<HTMLElement>('[data-comp-estado]')
    if (!fila || !valor) continue

    // Un nivel que no se reconoce se queda en "sin datos" en lugar de escribirse
    // tal cual: la fila la lee alguien que quiere saber si puede jugar, y una
    // palabra suelta que no significa nada es peor que admitir que no se sabe.
    //
    // Un componente AUSENTE del array cae aqui tambien, y es la via por la que
    // el publicador dice "no he podido medir esto": preferible a inventarse un
    // nivel.
    if (!esNivel(componente.estado)) continue

    fila.dataset.nivel = componente.estado
    valor.textContent = ETIQUETA[componente.estado]
  }
}

/**
 * El pie con la fecha de publicacion, y el aviso de que esta pasada de fecha.
 *
 * Se pinta SIEMPRE que el sello se pueda leer, vigente o no. Cuando el fichero
 * ha caducado es justo cuando mas falta hace ver "ultima actualizacion: 12/08
 * 03:11" debajo de cuatro filas en blanco: sin esa linea, "sin datos" no se
 * distingue de una pagina que todavia no ha terminado de cargar.
 */
function pintarPie(vig: Vigencia): void {
  if (!pie || !fecha || !vig.sello) return

  // Fecha absoluta y no "hace 5 minutos": lo relativo se calcula contra el reloj
  // del visitante, que puede estar mal, y entonces el panel miente con mucha
  // seguridad. Con la fecha escrita, un reloj desajustado se nota.
  fecha.textContent = vig.sello.toLocaleString(idioma === 'en' ? 'en-GB' : 'es-ES', {
    dateStyle: 'short',
    timeStyle: 'short',
  })

  pie.dataset.caducado = vig.vigente ? 'false' : 'true'
  if (caducado) caducado.hidden = vig.vigente

  pie.hidden = false
}

async function comprobar(): Promise<void> {
  // `no-store`: el aviso solo sirve si llega al momento. Nginx ya manda
  // `Cache-Control: no-store` para esta ruta; esto cubre el caso de un
  // intermediario que no lo respete.
  const respuesta = await fetch('/status.json', { cache: 'no-store' })

  // Un 404 aqui es la respuesta CORRECTA mientras nadie haya publicado nunca:
  // el fichero se sirve de una carpeta montada que empieza vacia. Salir sin
  // tocar nada deja las cuatro filas en "sin datos", que es exactamente lo que
  // la pagina sabe en ese momento.
  if (!respuesta.ok) return

  const estado: Estado = await respuesta.json()
  const vig = vigencia(estado)

  pintarAviso(estado)
  if (vig.vigente) pintarComponentes(estado)
  pintarPie(vig)
}

// El `catch` vacio es deliberado y es la regla de arriba escrita en codigo: un
// fallo al leer el estado no puede degradar la pagina.
void comprobar().catch(() => {})
